/**
 * הוצאות וספקים — "על מה אני מוציא, מה חורג מהרגיל, ואיזו הוצאה חסרה לה חשבונית"
 * (spec-business §4.5).
 *
 * A: 3 אריחים · B: קטגוריות מול בסיס (5) + C8 6 חודשים (7) · C: ספקים חוזרים (6) + טבלת הוצאות (6).
 * The baseline marker is prorated in a running month (and, for the fixed-supplier categories,
 * follows the suppliers' charge calendar); the full-month baseline is the second, fainter mark.
 */

import { useState } from 'react';
import type { ExpenseCategory, ExpensesQuery, ExpensesResponse, SuppliersResponse, SummaryResponse } from '../../../api-types.ts';
import { fmtDate, fmtMoney, fmtShortDate, monthName, monthShort } from '../format.ts';
import { CATEGORY, PAYMENT_CHANNEL, he } from '../i18n/he.ts';
import { CategoryIcon } from '../../../../shared/ui/src/index.tsx';
import type { GlyphName } from '../../../../shared/ui/src/index.tsx';
import { Icon } from '../components/Icon.tsx';
import { Card, CardHead, Chip, Delta, EmptyState, Mask, Money, Num, SearchField, SelectField, Skeleton, Tile } from '../components/ui.tsx';
import { InvoiceCell } from '../components/tx.tsx';
import { BarList } from '../charts/BarList.tsx';
import { ColumnChart } from '../charts/ColumnChart.tsx';
import { useApp } from '../hooks/useApp.tsx';
import { cacheKey } from '../hooks/cache.ts';
import { useQuery } from '../hooks/useQuery.ts';
import { useRouter } from '../hooks/useRouter.tsx';
import { errorMessage } from '../api-select.ts';

/** C8 folds TELECOM + BOOKKEEPING into "קבועות אחרות" to stay within 6 categorical hues (§6). */
const C8_SERIES: Array<{ key: string; label: string; series: number; cats: ExpenseCategory[] }> = [
  { key: 'EQUIPMENT', label: CATEGORY.EQUIPMENT.label, series: 1, cats: ['EQUIPMENT'] },
  { key: 'CONTRACTORS', label: CATEGORY.CONTRACTORS.label, series: 2, cats: ['CONTRACTORS'] },
  { key: 'SOFTWARE', label: CATEGORY.SOFTWARE.label, series: 3, cats: ['SOFTWARE'] },
  { key: 'VEHICLE', label: CATEGORY.VEHICLE.label, series: 4, cats: ['VEHICLE'] },
  { key: 'MARKETING', label: CATEGORY.MARKETING.label, series: 5, cats: ['MARKETING'] },
  { key: 'OTHER', label: he.expenses.otherFixed, series: 6, cats: ['TELECOM', 'BOOKKEEPING'] },
];

const THRESHOLDS: Array<[string, number | null]> = [
  ['10%', 10],
  ['20%', 20],
  ['30%', 30],
  ['50%', 50],
  [he.expenses.thresholdNone, null],
];

export function ExpensesScreen({ summary }: { summary: SummaryResponse | undefined }) {
  const { api, period, toast, openMenu, closeLayer } = useApp();
  const { route } = useRouter();
  const [cat, setCat] = useState<string>(route.query.cat ?? 'all');
  const [invoice, setInvoice] = useState<'all' | 'missing' | 'foreign'>(route.query.noinvoice === '1' ? 'missing' : 'all');
  const [channel, setChannel] = useState<'all' | 'card' | 'bank_transfer'>('all');
  const [q, setQ] = useState('');

  const query: ExpensesQuery = {
    period: route.query.period ?? period,
    cat: cat === 'all' ? undefined : cat,
    invoice,
    channel: channel === 'all' ? undefined : (channel as ExpensesQuery['channel']),
    q: q || undefined,
  };
  const expenses = useQuery<ExpensesResponse>(cacheKey('expenses', { ...query }), () => api.expenses(query), { scopes: ['expenses'] });
  const suppliers = useQuery<SuppliersResponse>(cacheKey('suppliers'), () => api.suppliers(), { scopes: ['suppliers'] });
  const asOf = summary?.asOf ?? '';
  const data = expenses.data;
  const partial = data?.period.partial ?? false;
  const summaryRow = data?.summary;

  const editThreshold = (category: ExpenseCategory, current: number, event: React.MouseEvent<HTMLButtonElement>) =>
    openMenu(
      event.currentTarget,
      <>
        <div className="hd">{he.expenses.editThreshold(CATEGORY[category].label, `${current}%`)}</div>
        {THRESHOLDS.map(([label, value]) => (
          <button
            key={label}
            type="button"
            role="menuitemradio"
            aria-checked={value === current}
            onClick={() => {
              closeLayer();
              api.updateExpenseCategory(category, { thresholdPct: value }).then(
                () => toast(he.expenses.thresholdSaved(CATEGORY[category].label, label)),
                (error: unknown) => toast(errorMessage(error), { error: true }),
              );
            }}
          >
            <Icon name="check" className="chk" />
            {label}
          </button>
        ))}
      </>,
      { label: he.expenses.editThreshold(CATEGORY[category].label, `${current}%`) },
    );

  const markInvoice = (txId: string) => {
    api.updateExpense(txId, { hasTaxInvoice: true }).then(
      () => toast(he.expenses.markedHasInvoice),
      (error: unknown) => toast(errorMessage(error), { error: true }),
    );
  };

  return (
    <div className="grid expenses">
      {/* ── אריחים ─────────────────────────────────────────────────── */}
      <div className="col-12 tiles t3">
        {/* the screen's ONE hero figure (REDESIGN §8); the baseline tile beside it is its comparison */}
        <Tile
          hero
          label={he.expenses.monthTitle(data ? (partial ? `${monthName(data.period.lastMonth, false)} (1–${data.period.dayOfMonth})` : monthName(data.period.lastMonth)) : '')}
          value={<Money agorot={summaryRow?.netAgorot ?? 0} />}
          note={he.expenses.monthNote}
        />
        <Tile
          label={partial ? he.expenses.baselineProrated(fmtShortDate(asOf, asOf)) : he.expenses.baselineFull}
          value={
            <>
              <Money agorot={(partial ? summaryRow?.proratedBaselineAgorot : summaryRow?.baselineAgorot) ?? 0} />
              {summaryRow && (partial ? summaryRow.proratedBaselineAgorot : summaryRow.baselineAgorot) ? (
                <>
                  <Delta
                    pct={
                      (summaryRow.netAgorot / (partial ? summaryRow.proratedBaselineAgorot : summaryRow.baselineAgorot) - 1) * 100
                    }
                    upGood={false}
                  />
                  <span className="unit">
                    {summaryRow.netAgorot >= (partial ? summaryRow.proratedBaselineAgorot : summaryRow.baselineAgorot) ? he.expenses.above : he.expenses.below}
                  </span>
                </>
              ) : null}
            </>
          }
          note={
            summaryRow
              ? partial
                ? he.expenses.baselineNoteCurrent(
                    fmtMoney(summaryRow.baselineAgorot),
                    summaryRow.baselineMonths.map((m) => monthShort(m, false)).join('–'),
                    data!.period.dayOfMonth,
                    data!.period.daysInMonth,
                  )
                : summaryRow.baselineAgorot
                  ? he.expenses.baselineNoteClosed
                  : he.expenses.baselineNoneNote
              : ''
          }
        />
        <Tile
          tone={summaryRow?.missing.count ? 'warn' : undefined}
          onClick={() => setInvoice(invoice === 'missing' ? 'all' : 'missing')}
          label={
            <>
              {partial ? he.expenses.missingTileCurrent : he.expenses.missingTile} <Icon name="flag" className="sm" />
            </>
          }
          value={
            <>
              <Num>{summaryRow?.missing.count ?? 0}</Num>
              {summaryRow?.missing.count ? <span className="unit">{fmtMoney(summaryRow.missing.netAgorot)}</span> : null}
            </>
          }
          note={
            summaryRow?.missingPrevMonth
              ? he.expenses.missingPrev(monthName(summaryRow.missingPrevMonth.month, false), summaryRow.missingPrevMonth.count, fmtMoney(summaryRow.missingPrevMonth.netAgorot))
              : he.expenses.missingFilterHint
          }
        />
      </div>

      {/* ── קטגוריות מול בסיס ──────────────────────────────────────── */}
      <Card span={5} labelledBy="h-ec">
        <CardHead titleId="h-ec" title={he.expenses.overQuestion} sub={partial ? he.expenses.overSubCurrent : he.expenses.overSubClosed} />
        {data ? (
          <BarList
            ariaLabel={he.expenses.barsAria}
            items={data.categories.map((stat) => {
              const compare = partial ? stat.proratedBaselineAgorot : stat.baselineAgorot;
              const overPct = stat.ratio == null ? 0 : (stat.ratio - 1) * 100;
              return {
                key: stat.category,
                label: (
                  <>
                    <CategoryIcon glyph={CATEGORY[stat.category].glyph as GlyphName} size={16} />
                    {CATEGORY[stat.category].label}
                  </>
                ),
                value: stat.amountAgorot,
                marker: compare || undefined,
                markerTitle: he.expenses.baselineToDate(fmtMoney(compare)),
                marker2: partial ? stat.baselineAgorot : undefined,
                marker2Title: he.expenses.baselineFullMarker(fmtMoney(stat.baselineAgorot)),
                pressed: cat === stat.category,
                onClick: () => setCat(cat === stat.category ? 'all' : stat.category),
                title: `${CATEGORY[stat.category].label}: ${fmtMoney(stat.amountAgorot)} · ${he.expenses.baselineToDate(fmtMoney(compare))}${partial ? ` · ${he.expenses.baselineFullMarker(fmtMoney(stat.baselineAgorot))}` : ''}`,
                valueLabel: (
                  <>
                    {stat.over ? (
                      <Chip tone={stat.severity === 'critical' ? 'critical' : 'warn'} icon="arrowUp" small>
                        +{Math.round(overPct)}%
                      </Chip>
                    ) : null}
                    <Money agorot={stat.amountAgorot} />{' '}
                    <span className="sub">
                      / {partial ? he.expenses.baselineToDate(fmtMoney(compare)) : he.expenses.baselineOf(fmtMoney(compare))}
                    </span>
                  </>
                ),
                extra: (
                  <button
                    type="button"
                    className="btn icon sm"
                    aria-haspopup="menu"
                    aria-label={he.expenses.editThreshold(CATEGORY[stat.category].label, `${stat.thresholdPct}%`)}
                    onClick={(event) => editThreshold(stat.category, stat.thresholdPct, event)}
                  >
                    <Icon name="pencil" className="sm" />
                  </button>
                ),
              };
            })}
          />
        ) : (
          <Skeleton className="ch" />
        )}
        <div className="note">{he.expenses.barsLegend}</div>
      </Card>

      {/* ── C8 ─────────────────────────────────────────────────────── */}
      <Card span={7} className="chart-c" labelledBy="h-c8">
        <CardHead titleId="h-c8" title={he.expenses.chartQuestion} sub={he.expenses.chartSub} />
        {data ? (
          <ColumnChart
            labels={data.monthly.map((m) => monthShort(m.month))}
            partialIndex={data.monthly.findIndex((m) => m.partial)}
            height={240}
            legendTwoCol
            series={C8_SERIES.map((s) => ({
              key: s.key,
              label: s.label,
              series: s.series,
              values: data.monthly.map((m) => s.cats.reduce((sum, c) => sum + (m.byCategory[c] ?? 0), 0)),
            }))}
            summary={`${he.expenses.chartQuestion} ${data.monthly.map((m) => `${monthShort(m.month)} ${fmtMoney(m.totalAgorot)}`).join(', ')}`}
            tip={(index) => {
              const month = data.monthly[index]!;
              return {
                title: monthName(month.month),
                rows: [
                  ...C8_SERIES.map((s) => ({
                    label: s.label,
                    value: fmtMoney(s.cats.reduce((sum, c) => sum + (month.byCategory[c] ?? 0), 0)),
                    color: `var(--series-${s.series})`,
                    square: true,
                  })),
                  { label: he.app.total, value: fmtMoney(month.totalAgorot) },
                ],
              };
            }}
            table={{
              caption: he.expenses.chartSub,
              head: ['חודש', ...C8_SERIES.map((s) => s.label), he.app.total],
              rows: data.monthly.map((m) => [
                monthName(m.month),
                ...C8_SERIES.map((s) => fmtMoney(s.cats.reduce((sum, c) => sum + (m.byCategory[c] ?? 0), 0))),
                fmtMoney(m.totalAgorot),
              ]),
            }}
          />
        ) : (
          <Skeleton className="ch" />
        )}
      </Card>

      {/* ── ספקים חוזרים ומנויים ───────────────────────────────────── */}
      <Card span={6} labelledBy="h-es">
        <CardHead titleId="h-es" title={he.expenses.suppliers} sub={he.expenses.suppliersSub} />
        <div className="tbl-wrap">
          <table className="tbl compact">
            <thead>
              <tr>
                <th scope="col">{he.expenses.colSupplier}</th>
                <th scope="col">{he.expenses.colCategory}</th>
                <th scope="col" className="n">
                  {he.expenses.colMonthly}
                </th>
                <th scope="col" className="n">
                  {he.expenses.colChargeDay}
                </th>
                <th scope="col">{he.expenses.colNextCharge}</th>
                <th scope="col">{he.expenses.colChannel}</th>
                <th scope="col">{he.expenses.colInvoiceMode}</th>
              </tr>
            </thead>
            <tbody>
              {(suppliers.data?.items ?? []).map((supplier) => (
                <tr key={supplier.id}>
                  <td>
                    {supplier.name}
                    {supplier.foreign ? <span className="sub">{he.expenses.foreignSupplier('')}</span> : null}
                  </td>
                  <td>
                    <Chip tone="outline" small>
                      {CATEGORY[supplier.category].label}
                    </Chip>
                  </td>
                  <td className="n">
                    <Money agorot={supplier.expectedMonthlyNetAgorot} />
                  </td>
                  <td className="n">
                    <Num>{supplier.chargeDay ?? '—'}</Num>
                  </td>
                  <td>{supplier.nextChargeDate ? <Num>{fmtShortDate(supplier.nextChargeDate, asOf)}</Num> : <span className="note">{he.app.none}</span>}</td>
                  <td>
                    {supplier.paymentChannel === 'card' ? (
                      <Mask>{`****${summary?.card?.account.numberLast4 ?? ''}`}</Mask>
                    ) : (
                      PAYMENT_CHANNEL[supplier.paymentChannel]
                    )}
                  </td>
                  <td>
                    {supplier.invoiceMode === 'auto' ? (
                      <Chip tone="good" icon="check" small>
                        {he.expenses.invoiceAuto}
                      </Chip>
                    ) : (
                      <Chip tone="outline" small>
                        {he.expenses.invoiceManual}
                      </Chip>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            {suppliers.data ? (
              <tfoot>
                <tr>
                  <td colSpan={2}>{he.expenses.fixedTotal}</td>
                  <td className="n">
                    <Money agorot={suppliers.data.totals.monthlyNetAgorot} />
                  </td>
                  <td colSpan={4}>
                    <span className="note">{he.expenses.fixedSplit(fmtMoney(suppliers.data.totals.cardAgorot), fmtMoney(suppliers.data.totals.bankAgorot))}</span>
                  </td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      </Card>

      {/* ── טבלת הוצאות ────────────────────────────────────────────── */}
      <Card span={6} labelledBy="h-et">
        <CardHead
          titleId="h-et"
          title={he.expenses.tableTitle(data ? monthName(data.period.lastMonth) : '')}
          sub={he.expenses.tableSub(data?.items.length ?? 0)}
        />
        <div className="filters">
          <SelectField
            name="cat"
            label={he.expenses.colCategory}
            value={cat}
            onChange={setCat}
            options={[['all', he.expenses.filterCatAll], ...(Object.keys(CATEGORY) as ExpenseCategory[]).map((key) => [key, CATEGORY[key].label] as const)]}
          />
          <SelectField
            name="invoice"
            label={he.cashflow.colInvoice}
            value={invoice}
            onChange={(value) => setInvoice(value as 'all' | 'missing' | 'foreign')}
            options={[
              ['all', he.expenses.filterInvAll],
              ['missing', he.expenses.filterInvMissing],
              ['foreign', he.expenses.filterInvForeign],
            ]}
          />
          <SelectField
            name="channel"
            label={he.expenses.colChannel}
            value={channel}
            onChange={(value) => setChannel(value as 'all' | 'card' | 'bank_transfer')}
            options={[
              ['all', he.expenses.filterChannelAll],
              ['card', he.expenses.filterChannelCard],
              ['bank_transfer', he.expenses.filterChannelBank],
            ]}
          />
          <SearchField value={q} onChange={setQ} placeholder={he.expenses.searchSupplier} label={he.expenses.searchSupplierAria} />
        </div>

        {expenses.loading ? (
          <Skeleton className="ch" />
        ) : !data?.items.length ? (
          <EmptyState
            good={invoice === 'missing'}
            text={invoice === 'missing' ? he.expenses.emptyMissing : summaryRow?.count ? he.expenses.emptyFilter : he.expenses.emptyMonth}
            action={
              <button
                type="button"
                className="btn sm"
                onClick={() => {
                  setCat('all');
                  setInvoice('all');
                  setChannel('all');
                  setQ('');
                }}
              >
                {he.app.clear}
              </button>
            }
          />
        ) : (
          <>
            <div className="tbl-wrap desk-only">
              <table className="tbl compact">
                <thead>
                  <tr>
                    <th scope="col">{he.expenses.colDate}</th>
                    <th scope="col">{he.expenses.colSupplier}</th>
                    <th scope="col">{he.expenses.colCategory}</th>
                    <th scope="col" className="n">
                      {he.expenses.colNet}
                    </th>
                    <th scope="col" className="n">
                      {he.expenses.colVat}
                    </th>
                    <th scope="col" className="n">
                      {he.expenses.colGross}
                    </th>
                    <th scope="col">{he.cashflow.colInvoice}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row) => (
                    <tr key={row.txId} className={row.status === 'PENDING' ? 'tr-pending' : undefined}>
                      <td>
                        <Num>{fmtDate(row.day)}</Num>
                      </td>
                      <td>
                        {row.merchant}
                        {row.status === 'PENDING' ? (
                          <>
                            {' '}
                            <Chip tone="warn" icon="clock" small>
                              {he.cashflow.pending}
                            </Chip>
                          </>
                        ) : null}
                      </td>
                      <td>{CATEGORY[row.expense.category].label}</td>
                      <td className="n">
                        <Money agorot={row.expense.netAgorot} decimals={2} />
                      </td>
                      <td className="n">
                        <Money agorot={row.expense.vatAgorot} decimals={2} />
                      </td>
                      <td className="n">
                        <Money agorot={row.expense.grossAgorot} decimals={2} />
                      </td>
                      <td>
                        <InvoiceCell expense={row.expense} onMark={() => markInvoice(row.txId)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3}>{he.app.total}</td>
                    <td className="n">
                      <Money agorot={summaryRow?.netAgorot ?? 0} decimals={2} />
                    </td>
                    <td className="n">
                      <Money agorot={summaryRow?.vatAgorot ?? 0} decimals={2} />
                    </td>
                    <td className="n">
                      <Money agorot={summaryRow?.grossAgorot ?? 0} decimals={2} />
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="list mob-only" style={{ flexDirection: 'column' }}>
              {data.items.map((row) => (
                <div className="list-item" role="listitem" key={row.txId}>
                  <div className="li-main">
                    <div className="li-t">
                      {row.merchant}
                      {row.status === 'PENDING' ? (
                        <Chip tone="warn" icon="clock" small>
                          {he.cashflow.pending}
                        </Chip>
                      ) : null}
                    </div>
                    <div className="li-s">
                      <Num>{fmtShortDate(row.day, asOf)}</Num> · {CATEGORY[row.expense.category].label} ·{' '}
                      {row.expense.foreign ? (
                        <Chip tone="outline" icon="globe" small>
                          {he.expenses.invoiceForeignShort}
                        </Chip>
                      ) : row.expense.hasTaxInvoice ? (
                        <Chip tone="good" icon="check" small>
                          {he.expenses.invoiceHas}
                        </Chip>
                      ) : (
                        <Chip tone="critical" icon="flag" small>
                          {he.expenses.invoiceMissingLong}
                        </Chip>
                      )}
                    </div>
                  </div>
                  <div className="li-end">
                    <span className="li-amt">
                      <Money agorot={row.expense.netAgorot} />
                    </span>
                    <span className="note">{he.expenses.grossOf(fmtMoney(row.expense.grossAgorot))}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
